import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import usePopup from "../../../../hooks/usePopup";
import { EmrContext } from "../../..";
import {
  BillingDateGroupType,
  useFetchBillingHistoryByEmrNo,
  useFetchEncounterDetailsByEmr,
} from "../../../../PatientCare/hookv2/apiHooks";

function BillingHistoryButton() {
  const { patientId } = useParams();
  const { patientData } = useContext(EmrContext);
  const { CustomModal, showSnackbar } = usePopup();
  const [openBilling, setOpenBilling] = useState(false);
  const [selectedBillingDate, setSelectedBillingDate] = useState("");

  const { mutate: fetchEncounterDates, data: encounterDateData } =
    useFetchEncounterDetailsByEmr();
  const {
    mutate: fetchBillingHistory,
    data: billingHistoryData,
    isLoading: isBillingLoading,
    reset: resetBillingHistory,
  } = useFetchBillingHistoryByEmrNo();

  const emrNo = useMemo(() => {
    const emrFromPatient = patientData?.pmaster?.patientId;
    if (emrFromPatient) return String(emrFromPatient);
    if (patientId) return String(patientId);
    return "";
  }, [patientData?.pmaster?.patientId, patientId]);

  const availableDates = useMemo(() => {
    const list = Array.isArray(encounterDateData)
      ? encounterDateData
      : encounterDateData?.response || [];
    const unique = new Set<string>();
    const out: string[] = [];
    list.forEach((item: any) => {
      const date = dayjs(item?.createdDate).isValid()
        ? dayjs(item.createdDate).format("YYYY-MM-DD")
        : "";
      if (date && !unique.has(date)) {
        unique.add(date);
        out.push(date);
      }
    });
    return out.sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf());
  }, [encounterDateData]);

  const billingDateData = useMemo(() => {
    const dates = billingHistoryData?.response?.dates || [];
    if (!dates.length) return null;
    if (!selectedBillingDate) return dates[0];
    return (
      dates.find(
        (item: BillingDateGroupType) =>
          dayjs(item?.date).format("YYYY-MM-DD") === selectedBillingDate,
      ) || null
    );
  }, [billingHistoryData?.response?.dates, selectedBillingDate]);

  const handleOpenBillingHistory = () => {
    if (!emrNo) {
      showSnackbar({
        message: "Unable to identify EMR number",
        open: true,
        variant: "warning",
        duration: 2500,
      });
      return;
    }
    setOpenBilling(true);
    fetchEncounterDates(emrNo);
    if (!billingHistoryData && !isBillingLoading) {
      fetchBillingHistory(emrNo);
    }
  };

  useEffect(() => {
    if (openBilling && !selectedBillingDate && availableDates.length > 0) {
      setSelectedBillingDate(availableDates[0]);
    }
  }, [openBilling, availableDates, selectedBillingDate]);

  useEffect(() => {
    setSelectedBillingDate("");
    setOpenBilling(false);
    resetBillingHistory();
  }, [emrNo, resetBillingHistory]);

  return (
    <>
      <Button
        onClick={handleOpenBillingHistory}
        endIcon={<ReceiptLongIcon />}
      >
        Billing History
      </Button>
      <CustomModal
        open={openBilling}
        setOpen={setOpenBilling}
        title={`Billing History${
          selectedBillingDate
            ? ` - ${dayjs(selectedBillingDate).format("DD-MM-YYYY")}`
            : ""
        }`}
      >
        <Box sx={{ p: 2, maxHeight: "75vh", overflowY: "auto", minWidth: "70vw" }}>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value={selectedBillingDate}
                displayEmpty
                onChange={(e) => setSelectedBillingDate(String(e.target.value))}
              >
                {!availableDates.length && (
                  <MenuItem value="">No date available</MenuItem>
                )}
                {availableDates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {dayjs(date).format("DD-MM-YYYY")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {isBillingLoading ? (
            <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : !billingDateData ? (
            <Typography variant="body2" color="text.secondary">
              No billing history found for this date.
            </Typography>
          ) : (
            <Box>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total CPT
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {Number(billingDateData.totalCptAmount || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Patient Share
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {Number(billingDateData.patientShareAmount || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Insurance Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {Number(billingDateData.totalInsuranceAmount || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Amount Paid
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {Number(billingDateData.totalAmountPayed || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
              {billingDateData.paydetails?.map((bill) => (
                <Paper
                  key={`${bill.billNo}-${bill.encounterId}`}
                  elevation={0}
                  sx={{ mb: 1.5, p: 1.25, border: "1px solid #eef2f6" }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.6 }}>
                    Bill #{bill.billNo} - {bill.doctorName || "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Encounter: {bill.encounterId} | Date:{" "}
                    {bill.date ? dayjs(bill.date).format("DD-MM-YYYY HH:mm") : "-"}
                  </Typography>
                  <Box sx={{ mt: 0.8 }}>
                    {(bill.allBillsByResponseDto || []).map((item) => (
                      <Box
                        key={item.billingLineId}
                        sx={{
                          py: 0.8,
                          borderBottom: "1px dashed #edf0f3",
                          "&:last-of-type": { borderBottom: "none" },
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {item.itemCode} - {item.itemName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Qty: {item.quantity} | Rate: {item.rate} | Total:{" "}
                          {item.totalAmount} | Pat Share: {item.patShare} | Ins:{" "}
                          {item.insAmount}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </CustomModal>
    </>
  );
}

export default BillingHistoryButton;
